import { CircleHelp, RefreshCw } from "lucide-react";
import { FC, useCallback, useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePreviewStore } from "@/hooks/use-preview-store";
import { LGT_VERIFY_URL } from "@/lib/env";
import { filteredLoginOptions } from "@/lib/loginOptions";
import { PreviewParams } from "@/lib/preview";

interface IFrameEvent {
  data: {
    ready: boolean;
  };
}

type Props = {
  params: PreviewParams;
};

export const PreviewExperience: FC<Props> = ({ params }) => {
  const { overrides, updateOverrides } = usePreviewStore();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleClaimedStatusChange = useCallback(
    (checked: boolean) => {
      updateOverrides({ isClaimed: checked });
    },
    [updateOverrides],
  );

  const handleSealedStatusChange = useCallback(
    (checked: boolean) => {
      updateOverrides({ isTamperSealed: checked });
    },
    [updateOverrides],
  );

  useEffect(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { tag: params, overrides },
        "*",
      );
    }

    const handleMessage = (event: IFrameEvent) => {
      if (event.data.ready && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { tag: params, overrides },
          "*",
        );
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [params, overrides]);

  const refreshPreview = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.src = LGT_VERIFY_URL;
    }
  }, []);

  const isLoginEnabled =
    filteredLoginOptions(params.login_options ?? []).length > 0;

  return (
    <div className="flex-1 max-w-[430px] min-w-[375px]">
      <div className="w-full rounded-3xl bg-secondary overflow-hidden border aspect-9/16">
        <iframe
          id="preview"
          ref={iframeRef}
          src={LGT_VERIFY_URL}
          title="Preview"
          className="size-full"
          allow="encrypted-media; clipboard-write"
        />
      </div>

      <div className="flex gap-5.5 mt-6">
        <Button className="flex-1" variant="secondary" onClick={refreshPreview}>
          <RefreshCw /> Refresh
        </Button>

        <div className="flex items-center gap-2">
          <Switch
            disabled={!isLoginEnabled}
            checked={isLoginEnabled ? overrides.isClaimed : false}
            onCheckedChange={handleClaimedStatusChange}
          />
          <div className="flex gap-1">
            <p className="font-medium text-sm">Claimed</p>
            <Tooltip>
              <TooltipTrigger>
                <CircleHelp className="size-4.5" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Preview the claimed and unclaimed states</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            checked={overrides.isTamperSealed}
            onCheckedChange={handleSealedStatusChange}
          />
          <div className="flex gap-1">
            <p className="font-medium text-sm">Sealed</p>
            <Tooltip>
              <TooltipTrigger>
                <CircleHelp className="size-4.5" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Preview the sealed and opened states for tabs tied to tamper
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};
